import { useCallback, useEffect, useState } from "react";
import { listToursRequest } from "@services/tours/toursApi";

// Supports different backend shapes while keeping the UI stable.
function normalizeTours(body) {
  if (Array.isArray(body?.tours)) return body.tours;
  if (Array.isArray(body?.items)) return body.items;
  if (Array.isArray(body)) return body;
  return [];
}

function normalizePagination(body = {}) {
  const source = body?.pagination || {};
  const page = Number(source?.page) || 1;
  const pageSize = Number(source?.pageSize) || 10;
  const total = Number(source?.total) || 0;
  const totalPages = Number(source?.totalPages) || 1;
  return {
    page,
    pageSize,
    total,
    totalPages,
    hasNextPage: Boolean(source?.hasNextPage),
    hasPreviousPage: Boolean(source?.hasPreviousPage),
  };
}

// Tour index data hook for map tools panel.
function useTours(initialPageSize = 10) {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(initialPageSize);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: initialPageSize,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // Shared fetch function used on mount and manual refresh.
  const fetchTours = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const response = await listToursRequest({ page, pageSize, search });
      setTours(normalizeTours(response.body));
      setPagination(normalizePagination(response.body));
    } catch (err) {
      setError(err.message || "Could not load tours.");
      setTours([]);
      setPagination((current) => ({
        ...current,
        page,
        pageSize,
      }));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    // Initial load.
    fetchTours();
  }, [fetchTours]);

  return {
    tours,
    loading,
    error,
    search,
    setSearch,
    page,
    setPage,
    pageSize,
    pagination,
    refreshTours: fetchTours,
  };
}

export default useTours;
